import React, { useState, useRef, useEffect } from 'react';
import './RichTextInput.css';

const RichTextInput = ({
  title,
  value = '',
  onChange,
  placeholder = 'Enter text...',
  required = false,
  disabled = false,
  error,
  className = "",
  rows = 6,
  ...props
}) => {
  const editorRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value;
    }
  }, [value]);

  const handleInput = () => {
    if (onChange && editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleInput();
  };

  const insertLink = () => {
    const url = prompt('Enter URL:');
    if (url) {
      execCommand('createLink', url);
    }
  };

  const insertImage = () => {
    const url = prompt('Enter image URL:');
    if (url) {
      execCommand('insertImage', url);
    }
  };

  const toolbarButtons = [
    { icon: 'B', command: 'bold', title: 'Bold' },
    { icon: 'I', command: 'italic', title: 'Italic' },
    { icon: 'U', command: 'underline', title: 'Underline' },
    { icon: 'S', command: 'strikeThrough', title: 'Strikethrough' },
    { separator: true },
    { icon: 'H1', command: 'formatBlock', value: '<h1>', title: 'Heading 1' },
    { icon: 'H2', command: 'formatBlock', value: '<h2>', title: 'Heading 2' },
    { icon: 'H3', command: 'formatBlock', value: '<h3>', title: 'Heading 3' },
    { separator: true },
    { icon: '•', command: 'insertUnorderedList', title: 'Bullet List' },
    { icon: '1.', command: 'insertOrderedList', title: 'Numbered List' },
    { separator: true },
    { icon: '🔗', command: 'link', title: 'Insert Link', custom: insertLink },
    { icon: '🖼️', command: 'image', title: 'Insert Image', custom: insertImage },
    { separator: true },
    { icon: '⬅️', command: 'justifyLeft', title: 'Align Left' },
    { icon: '➡️', command: 'justifyRight', title: 'Align Right' },
    { icon: '↔️', command: 'justifyCenter', title: 'Align Center' },
    { separator: true },
    { icon: '↩️', command: 'undo', title: 'Undo' },
    { icon: '↪️', command: 'redo', title: 'Redo' },
  ];

  return (
    <div className={`rich-text-input-container ${className}`}>
      {title && (
        <label className="rich-text-input-title">
          {title}
          {required && <span className="required-asterisk">*</span>}
        </label>
      )}
      
      <div className={`rich-text-input-wrapper ${isFocused ? 'focused' : ''}`}>
        {/* Toolbar */}
        <div className="rich-text-toolbar">
          {toolbarButtons.map((button, index) => (
            <React.Fragment key={index}>
              {button.separator ? (
                <div className="toolbar-separator" />
              ) : (
                <button
                  type="button"
                  className="toolbar-button"
                  onClick={() => button.custom ? button.custom() : execCommand(button.command, button.value)}
                  title={button.title}
                  disabled={disabled}
                >
                  {button.icon}
                </button>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Editor */}
        <div
          ref={editorRef}
          className="rich-text-editor"
          contentEditable={!disabled}
          onInput={handleInput}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          style={{ minHeight: `${rows * 1.5}em` }}
          {...props}
        />
      </div>

      {error && (
        <div className="rich-text-input-error-message">
          {error}
        </div>
      )}
    </div>
  );
};

export default RichTextInput;
